/**
 * Nutrition Slice 3 — Foliar Application Sessions
 *
 * Service covering the full session lifecycle:
 *   - convertRound: materialise per-orchard sessions from a foliar round
 *   - getSessions / getSession: list & detail
 *   - assignCombo / removeCombo: operator×machine assignment
 *   - getMixPreview: N·P·K tank-mix preview (offline preview before start)
 *   - startCombo / completeCombo: offline-first field operations
 *   - signOff / unlockProgramme: manager lifecycle
 *
 * Machine and operator lists reuse the Protection (spray) pool read-only;
 * no new endpoints are added for those.
 */
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { db } from '../db';
import { sprayService } from './sprayService';
import type { SprayMachine, SprayOperator } from './sprayService';
import type { ComboWeatherSnapshot } from '../db';

// ─── Re-exports for callers that want the pool reads under this namespace ────

export type { SprayMachine, SprayOperator };

export const getMachines = (): Promise<SprayMachine[]> => sprayService.getMachines();
export const getOperators = (): Promise<SprayOperator[]> => sprayService.getOperators();

// ─── Auth ─────────────────────────────────────────────────────────────────────

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const base = '/api/v1/nutrition';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Flat worklist item returned by GET /nutrition/sessions/my.
 * Covers both foliar (kind='foliar', id=comboId) and soil (kind='soil', id=sessionId).
 * NOTE: programmeName (double-m-e) matches the backend field exactly.
 */
export interface NutritionMyWorkItem {
  kind: 'foliar' | 'soil';
  id: string;            // comboId (foliar) | sessionId (soil)
  sessionId: string;
  seq: number;
  name: string;
  programmeName: string;
  roundName: string;
  orchardOrGroup: string;
  orchardCount: number;
  machineName: string | null;
  /** The PARENT session's derived status. Kept on the wire; rendered by nothing. */
  status: string;
  /** The combo's own status — what the card renders. Absent on rows cached before F5. */
  comboStatus?: string;
  cultivar?: string | null;
  areaHa?: number | null;
  tanksPlanned?: number | null;
  date: string | null;
}

/**
 * The parent session's plan — MACHINE-INDEPENDENT facts only.
 *
 * `tankCount` and `perTankMix` were declared here as required fields until
 * #461. They never arrive: a session is converted before any machine is
 * known, so the per-tank figures are written per COMBO by assignCombo (see
 * `FoliarRosterRow.tankVolumeL` in nutritionService.ts for the full rule),
 * and the boot migration strips the legacy keys from every stored foliar
 * session. Declaring them here told the compiler to expect a shape the API
 * cannot send.
 */
export interface NutritionPlannedMix {
  totalWaterL: number;
  productTotals: Array<{
    productId: string;
    productName: string;
    totalQty: number;
    unit: string;
    n: number;
    p: number;
    k: number;
  }>;
}

// ─── Soil batch types ─────────────────────────────────────────────────────────

export interface NutritionSoilBatchProductTotal {
  productId: string;
  productName: string;
  totalQty: number;
  unit: string;
  n: number;
  p: number;
  k: number;
}

export interface NutritionSoilBatchPerOrchard {
  orchardId: string;
  orchardNo: string;
  ha: number;
  products: NutritionSoilBatchProductTotal[];
}

export interface NutritionSoilBatch {
  totalWaterL: number;
  productTotals: NutritionSoilBatchProductTotal[];
  perOrchard?: NutritionSoilBatchPerOrchard[];
}

export interface NutritionAssignedOperator {
  operatorId: string;
  operatorName: string;
}

export interface NutritionSession {
  _id: string;
  programmeId: string;
  roundId: string;
  roundName: string;
  type: 'foliar' | 'soil';
  seq: number;
  name: string;
  status: 'draft' | 'incomplete' | 'planned' | 'in_progress' | 'completed' | 'signed_off' | 'deleted' | 'cancelled';
  orchardNo: string;
  date: string;
  plannedMix?: NutritionPlannedMix;
  startedAt?: string;
  completedAt?: string;
  signedOffAt?: string;
  captureMode?: 'live' | 'back_capture';
  // Soil-specific fields
  orchardIds?: string[];
  groupId?: string;
  groupLabel?: string;
  assignedOperators?: NutritionAssignedOperator[];
  actuals?: NutritionSoilBatch;
}

export interface NutritionComboProductConsumed {
  productId: string;
  quantity: number;
  unit?: string;
}

export interface NutritionCombo {
  _id: string;
  sessionId: string;
  operatorId: string;
  machineId: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'incomplete' | 'cancelled';
  startedAt?: string;
  endedAt?: string;
  productsConsumed: NutritionComboProductConsumed[];
  tankCount: number;
  weatherSnapshots?: { start?: ComboWeatherSnapshot; end?: ComboWeatherSnapshot };
}

export interface NutritionMixPreviewLine {
  productId: string;
  productName: string;
  dosePerTank: number;
  doseUnit: string;
  n: number;
  p: number;
  k: number;
}

export interface NutritionMixPreview {
  machine: { _id: string; name: string; tankVolume: number };
  totalWaterL: number;
  /** Ceiling — how many times the operator fills the machine. */
  tankCount: number;
  /**
   * The EXACT tank fraction (totalWaterL / tankVolume), e.g. 0.4.
   *
   * Consumption follows THIS, not `tankCount`: a part tank's balance is
   * sprayed on the next orchard, so an orchard takes its fraction rather than
   * a whole tank. `dosePerTank` is a full tank's worth — a MIXING figure — so
   * dosePerTank x tanksExact returns exactly the orchard total.
   *
   * Optional: sessions written before the full-tank fix have no value here,
   * and callers fall back to `tankCount`.
   */
  tanksExact?: number;
  perTankMix: NutritionMixPreviewLine[];
  /**
   * How many operators share this session, cancelled combos excluded (#506).
   *
   * `tanksExact` is the WHOLE SESSION's tank fraction. Seeding the tanks box
   * with it is right for one operator and is exactly the doubling bug for two,
   * because each operator would then submit the whole session's work as their
   * own portion. Optional: a cached preview taken before this shipped has no
   * value, and callers must treat an absent count as "unknown", never as 1.
   */
  activeComboCount?: number;
}

export interface NutritionSessionSummary {
  session: NutritionSession;
  comboCount: number;
  operatorNames: string[];
}

// ─── Offline queue types (parallel shapes to operatorComboStarts/Completes) ──

export interface NutritionComboStartPayload {
  startedAt: string;
  weatherStart?: ComboWeatherSnapshot;
}

export interface NutritionComboCompletePayload {
  endedAt: string;
  tankCount: number;
  /**
   * Optional on the wire (#506, D1) — mirrors spray's Task 6.7 precedent
   * (operatorSpraySessionService.completeCombo): the server now recomputes
   * this itself from `tankCount x dosePerTank` and discards whatever the
   * client sends, so the field page (NutritionFoliarFieldPage) no longer
   * populates it at all.
   */
  productsConsumed?: NutritionComboProductConsumed[];
  weatherEnd?: ComboWeatherSnapshot;
}

// ─── Retryable error check (mirrors spraySessionStartService) ─────────────────

function isRetryableError(err: any): boolean {
  if (!err) return false;
  if (err.response) return false;
  if (err.cause && err.cause.response) return false;
  return true;
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const nutritionSessionService = {
  /**
   * Materialise per-orchard foliar sessions from a round.
   * POST /nutrition/programmes/:id/rounds/:roundId/convert
   */
  async convertRound(
    programmeId: string,
    roundId: string,
    orchardNos: string[],
  ): Promise<{ created: NutritionSession[]; skipped: Array<{ orchardNo: string; reason: string }> }> {
    try {
      const res = await axios.post(
        `${base}/programmes/${programmeId}/rounds/${roundId}/convert`,
        { orchardNos },
        { headers: authHeaders() },
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * List sessions, optionally filtered by programme and/or round.
   * GET /nutrition/sessions?programmeId&roundId
   */
  async getSessions(programmeId?: string, roundId?: string): Promise<NutritionSessionSummary[]> {
    const params: Record<string, string> = {};
    if (programmeId) params.programmeId = programmeId;
    if (roundId) params.roundId = roundId;
    try {
      const res = await axios.get<NutritionSessionSummary[]>(`${base}/sessions`, {
        headers: authHeaders(),
        params,
      });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Session detail including combos and consumption transactions.
   * GET /nutrition/sessions/:id
   */
  async getSession(id: string): Promise<{
    session: NutritionSession;
    combos: NutritionCombo[];
    consumptionTransactions: unknown[];
  }> {
    try {
      const res = await axios.get(`${base}/sessions/${id}`, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Assign an operator×machine combo to a session.
   * POST /nutrition/sessions/:sessionId/combos
   */
  async assignCombo(
    sessionId: string,
    input: { operatorId: string; machineId: string },
  ): Promise<{ combo: NutritionCombo; parent: NutritionSession }> {
    try {
      const res = await axios.post(
        `${base}/sessions/${sessionId}/combos`,
        input,
        { headers: authHeaders() },
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Get a single combo by id.
   * GET /nutrition/operator-sessions/:id
   */
  async getCombo(comboId: string): Promise<NutritionCombo> {
    try {
      const res = await axios.get<NutritionCombo>(
        `${base}/operator-sessions/${comboId}`,
        { headers: authHeaders() },
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Remove a combo from its parent session.
   * DELETE /nutrition/operator-sessions/:id
   */
  async removeCombo(comboId: string): Promise<{ parent: NutritionSession }> {
    try {
      const res = await axios.delete(`${base}/operator-sessions/${comboId}`, {
        headers: authHeaders(),
      });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * POST /nutrition/operator-sessions/:id/cancel — stand an assigned operator
   * down (#476). Manager-only; the server refuses a combo that has captured
   * actuals (HAS_ACTUALS) and the last combo of an untouched session
   * (LAST_COMBO), both as 409.
   */
  async cancelCombo(comboId: string, reason: string): Promise<{ combo: NutritionCombo; parent: NutritionSession }> {
    try {
      const res = await axios.post(
        `${base}/operator-sessions/${comboId}/cancel`,
        { reason },
        { headers: authHeaders() },
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Get the N·P·K per-tank mix preview for a combo.
   * GET /nutrition/operator-sessions/:id/mix-preview
   */
  async getMixPreview(comboId: string): Promise<NutritionMixPreview> {
    try {
      const res = await axios.get<NutritionMixPreview>(
        `${base}/operator-sessions/${comboId}/mix-preview`,
        { headers: authHeaders() },
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Offline-first start: try network first; on network failure queue in Dexie
   * for later replay by nutritionOperatorSessionSyncService.
   * POST /nutrition/operator-sessions/:id/start
   */
  async startCombo(comboId: string, payload: NutritionComboStartPayload): Promise<void> {
    return Sentry.startSpan(
      { name: 'nutrition.combo.start.queueOrSend', op: 'http.client', attributes: { comboId } },
      async () => {
        try {
          await axios.post(
            `${base}/operator-sessions/${comboId}/start`,
            payload,
            { headers: authHeaders() },
          );
        } catch (err: any) {
          if (!isRetryableError(err)) {
            throw err;
          }
          // Network failure — queue for later replay.
          await db.nutritionComboStarts.add({
            comboId,
            payload,
            syncStatus: 'pending',
            createdAt: new Date().toISOString(),
          });
        }
      },
    );
  },

  /**
   * Offline-first complete: try network first; on network failure queue in Dexie
   * for later replay by nutritionOperatorSessionSyncService.
   * POST /nutrition/operator-sessions/:id/complete
   */
  async completeCombo(comboId: string, payload: NutritionComboCompletePayload): Promise<void> {
    return Sentry.startSpan(
      { name: 'nutrition.combo.complete.queueOrSend', op: 'http.client', attributes: { comboId } },
      async () => {
        try {
          await axios.post(
            `${base}/operator-sessions/${comboId}/complete`,
            payload,
            { headers: authHeaders() },
          );
        } catch (err: any) {
          if (!isRetryableError(err)) {
            throw err;
          }
          // Network failure — queue for later replay.
          await db.nutritionComboCompletes.add({
            comboId,
            payload,
            syncStatus: 'pending',
            createdAt: new Date().toISOString(),
          });
        }
      },
    );
  },

  /**
   * Manager sign-off: transitions session to signed_off.
   * POST /nutrition/sessions/:id/signoff
   */
  async signOff(sessionId: string): Promise<NutritionSession> {
    try {
      const res = await axios.post(
        `${base}/sessions/${sessionId}/signoff`,
        {},
        { headers: authHeaders() },
      );
      // Route returns { ok: true, session } — unwrap the session object.
      return (res.data as { ok: boolean; session: NutritionSession }).session;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Unlock a programme (revert from active to reviewed so rounds can be edited).
   * POST /nutrition/programmes/:id/unlock
   */
  async unlockProgramme(programmeId: string): Promise<unknown> {
    try {
      const res = await axios.post(
        `${base}/programmes/${programmeId}/unlock`,
        {},
        { headers: authHeaders() },
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Soft-delete a session. The server reverses stock for any session that
   * consumed and requires an explicit confirmation before it will (#485).
   * NOTE: axios.delete takes a request body under `data`, not positionally.
   * DELETE /nutrition/sessions/:id
   */
  async removeSession(sessionId: string): Promise<unknown> {
    try {
      const res = await axios.delete(`${base}/sessions/${sessionId}`, {
        headers: authHeaders(),
        data: { confirm: true },
      });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Return the operator's personal worklist — all planned/in-progress
   * nutrition sessions (foliar combos + soil sessions) assigned to them.
   * GET /nutrition/sessions/my
   */
  async getMySessions(): Promise<{ items: NutritionMyWorkItem[]; hasLinkedOperator: boolean }> {
    const res = await axios.get<{ items: NutritionMyWorkItem[]; hasLinkedOperator: boolean }>(
      `${base}/sessions/my`,
      { headers: authHeaders() },
    );
    return res.data;
  },

  /**
   * Download and open the printable instruction PDF for a session.
   * GET /nutrition/sessions/:id/instruction/pdf  (foliar)
   * GET /nutrition/soil-sessions/:id/instruction/pdf  (soil)
   */
  async downloadInstructionPdf(kind: 'foliar' | 'soil', id: string): Promise<void> {
    const path = kind === 'soil' ? `soil-sessions/${id}` : `sessions/${id}`;
    const res = await axios.get(`${base}/${path}/instruction/pdf`, {
      headers: authHeaders(),
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};
